import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Rocket, Users, FileSignature, MessagesSquare, CalendarDays, TrendingUp, ChevronRight } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { DASH } from '@/constants/testIds';

function Kpi({ label, value, hint, icon: Icon, to, testid }) {
  const card = (
    <div className="ve-card p-6 group transition-all hover:border-[#8B3FC5] hover:shadow-lg" data-testid={testid || DASH.overviewKpi}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.18em] text-slate-500">{label}</span>
        <span className="h-9 w-9 rounded-md bg-[#8B3FC5]/10 text-[#8B3FC5] flex items-center justify-center transition-transform group-hover:scale-110">
          <Icon size={16} strokeWidth={1.5}/>
        </span>
      </div>
      <div className="display text-4xl mt-3">{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1.5 flex items-center gap-1">
        <span>{hint}</span>
        {to && <ChevronRight size={11} className="opacity-0 group-hover:opacity-100 transition-opacity text-[#8B3FC5]"/>}
      </div>}
    </div>
  );
  if (!to) return card;
  return <Link to={to} aria-label={`Open ${label}`} className="block">{card}</Link>;
}

export default function Overview() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ startups: 0, agreements: 0, meetings: 0, threads: 0 });
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, a, m, t] = await Promise.all([
          api.get('/startups/'),
          api.get('/startups/agreements'),
          api.get('/meetings/'),
          api.get('/chat/threads'),
        ]);
        setStats({
          startups: s.data.length,
          agreements: a.data.length,
          meetings: m.data.length,
          threads: t.data.length,
        });
        setRecent(s.data.slice(0, 6));
      } catch (err) {
        console.error('Overview: stats load failed', err);
      }
    })();
  }, []);

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="ve-eyebrow">Dashboard</div>
          <h1 className="display text-2xl sm:text-3xl md:text-4xl mt-2">Welcome back, {user?.name?.split(' ')[0]}.</h1>
          <p className="text-sm text-slate-600 mt-2">Live overview of your governance, venture, and partnership pipeline.</p>
        </div>
        <Link to="/dashboard/startups" className="ve-btn-primary self-start text-xs sm:text-sm">
          Browse startups <ChevronRight size={14}/>
        </Link>
      </div>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
        <Kpi label="Startups"  value={stats.startups}   hint="Vetted + in-pipeline"          icon={Rocket}          to="/dashboard/startups"   testid="overview-kpi-startups"/>
        <Kpi label="Agreements" value={stats.agreements} hint="NDA / MOU active or pending"  icon={FileSignature}   to="/dashboard/agreements" testid="overview-kpi-agreements"/>
        <Kpi label="Meetings"   value={stats.meetings}   hint="Including pending approvals"  icon={CalendarDays}    to="/dashboard/meetings"   testid="overview-kpi-meetings"/>
        <Kpi label="Compliance threads" value={stats.threads} hint="PII-redacted conversations" icon={MessagesSquare} to="/dashboard/chat"      testid="overview-kpi-chat"/>
      </div>

      <div className="mt-8 sm:mt-10 grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <div className="lg:col-span-2 ve-card p-6">
          <div className="flex items-center justify-between">
            <h2 className="display text-xl">Recent startups</h2>
            <Link to="/dashboard/startups" className="ve-btn-ghost">View all <ChevronRight size={14}/></Link>
          </div>
          <div className="mt-5 divide-y divide-slate-100">
            {recent.length === 0 && (
              <div className="text-sm text-slate-500 py-6 text-center">
                No startups yet. <Link to="/dashboard/startups" className="text-[#8B3FC5] font-medium">Add the first one →</Link>
              </div>
            )}
            {recent.map((s) => (
              <Link key={s.startup_id} to={`/dashboard/startups/${s.startup_id}`} className="flex items-center justify-between py-3 hover:bg-slate-50 px-2 rounded-md transition-colors">
                <div>
                  <div className="text-sm font-medium">{s.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{s.industry} · {s.stage} · {s.location || '—'}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs px-2 py-1 rounded uppercase tracking-[0.15em] ${
                    s.vetting_status === 'approved' ? 'bg-emerald-50 text-emerald-700' :
                    s.vetting_status === 'rejected' ? 'bg-rose-50 text-rose-700' :
                    'bg-amber-50 text-amber-700'
                  }`}>{s.vetting_status}</span>
                  <span className="display text-lg text-[#8B3FC5]">{s.ai_score ? Math.round(s.ai_score) : '—'}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="ve-card p-6">
          <div className="ve-eyebrow">Quick actions</div>
          <h2 className="display text-xl mt-2">Move work forward</h2>
          <div className="mt-5 space-y-2">
            <Link to="/dashboard/startups" className="ve-side-link"><Rocket size={16}/>Submit / vet a startup</Link>
            <Link to="/dashboard/meetings" className="ve-side-link"><CalendarDays size={16}/>Schedule a meeting</Link>
            <Link to="/dashboard/agreements" className="ve-side-link"><FileSignature size={16}/>Generate NDA / MOU</Link>
            <Link to="/dashboard/chat" className="ve-side-link"><MessagesSquare size={16}/>Open compliance chat</Link>
            <Link to="/dashboard/entities" className="ve-side-link"><Users size={16}/>Add an entity</Link>
          </div>
          <div className="mt-6 ve-silver-divider"/>
          <div className="mt-6 flex items-center gap-3 text-xs text-slate-500">
            <TrendingUp size={14} className="text-[#8B3FC5]"/>
            <span>System status: <span className="text-emerald-700 font-medium">Operational</span></span>
          </div>
        </div>
      </div>
    </div>
  );
}
